import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardBody } from '@heroui/card';
import { Button } from '@heroui/button';
import { Slider } from '@heroui/slider';
import { IoPlay, IoRepeat, IoStop } from 'react-icons/io5';
import { PitchShifter } from 'soundtouchjs';
import { PiMetronomeFill } from 'react-icons/pi';

import { useDecodeAudio, useUpdateSongMediaOptions } from '@/hooks/useSongbook.ts';
import { SongMediaAudio } from '@/core/db.ts';

interface MediaAudioProps {
  mediaFile: SongMediaAudio;
}

function formatSeconds(seconds = 0, multiplier = 1) {
  const date = new Date(0);

  date.setSeconds(seconds * multiplier);

  if (seconds < 3600) {
    return date.toISOString().substring(14, 19);
  }

  return date.toISOString().substring(11, 19);
}

function MediaAudio({ mediaFile }: MediaAudioProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioOffset = useRef(0);
  const audioIsSeeking = useRef(false);
  const audioRepeat = useRef<boolean>(false);
  const audioContext = useRef<AudioContext>();
  const audioNode = useRef<AudioBufferSourceNode>();
  const audioShifter = useRef<PitchShifter>();
  const [progress, setProgress] = useState(0);

  const [isRepeat, setIsRepeat] = useState(mediaFile.options?.repeat || false);
  const [speed, setSpeed] = useState(mediaFile.options?.speed || 1);

  const { data: decodedAudio } = useDecodeAudio(mediaFile);
  const { mutate: mutateOptions } = useUpdateSongMediaOptions();

  const updateOptions = useCallback((options: SongMediaAudio['options']) => {
    mutateOptions({ id: mediaFile.id, options: { ...mediaFile.options, ...options }});
  }, [mediaFile.id, mediaFile.options, mutateOptions]);

  const handleStop = useCallback(() => {
    setIsPlaying(false);

    setProgress(0);
    audioNode.current?.stop();
    audioShifter.current?.disconnect();
  }, []);

  useEffect(() => handleStop, [handleStop]);

  const handleChangeSpeed = useCallback((speed: number) => {
    setSpeed(speed);
    handleStop();
    updateOptions({ speed });
  }, [handleStop, updateOptions, mediaFile.id]);

  const handlePlay = useCallback((offset = 0) => {
    if (!decodedAudio) return;

    audioOffset.current = offset;

    if (isPlaying) {
      audioNode.current?.stop();
    }

    setIsPlaying(true);

    audioShifter.current?.off();
    audioNode.current = undefined;
    audioShifter.current = undefined;

    audioContext.current = new window.AudioContext();

    if (speed !== 1) {
      audioShifter.current = new PitchShifter(audioContext.current, decodedAudio, 16384, () => {
        if (!audioShifter.current) return;

        if (audioRepeat.current) {
          audioShifter.current.percentagePlayed = 0;
        } else {
          setIsPlaying(false);
          audioShifter.current?.disconnect();
        }
      });

      audioShifter.current.tempo = speed;
      audioShifter.current.pitch = 1;

      audioShifter.current.connect(audioContext.current.destination);
    } else {
      audioNode.current = audioContext.current.createBufferSource();
      audioNode.current.buffer = decodedAudio;
      audioNode.current.connect(audioContext.current.destination);

      audioNode.current.loop = isRepeat;

      audioNode.current.addEventListener('ended', () => {
        if (!audioIsSeeking.current) {
          handleStop();
        }

        audioIsSeeking.current = false;
      });

      audioNode.current.start(0, offset);
    }
  }, [decodedAudio, isRepeat, speed, isPlaying, handleStop]);

  useEffect(() => {
    audioRepeat.current = isRepeat;

    updateOptions({ repeat: isRepeat });

    if (!audioNode.current) return;
    audioNode.current.loop = isRepeat;
  }, [isRepeat]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying && audioContext.current ) {
        let p = 0;

        const currentTime = audioOffset.current + audioContext.current.currentTime;

        if (audioNode.current && audioNode.current.buffer) {
          p = (currentTime / (audioNode.current.buffer.duration / speed)) % 1;
        } else if (audioShifter.current && audioShifter.current.timePlayed) {
          p = audioShifter.current.percentagePlayed / 100;
        }

        if (p <= 1) {
          setProgress(p);
        }
      }
    }, 10);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const onHandleSeek = useCallback((newProgress: number | number[]) => {
    const progress = typeof newProgress === 'number' ? newProgress : newProgress[0];

    if (audioNode.current) {
      const offset = (audioNode.current.buffer?.duration || 0) * progress;

      audioIsSeeking.current = true;

      handlePlay(offset);
    } else if (audioShifter.current) {
      audioShifter.current.percentagePlayed = progress;
    }
  }, [handlePlay]);

  return (
    <Card className="w-full max-w-150">
      <CardBody className="flex flex-col gap-4">
        <div className="flex gap-4 justify-between items-center">
          <div className="flex gap-2">
            <Button
              isIconOnly
              size="lg"
              title={isPlaying ? t('media.audio.stop') : t('media.audio.play')}
              onPress={isPlaying ? handleStop : () => handlePlay()}
            >
              {isPlaying ? <IoStop /> : <IoPlay />}
            </Button>
            <Button
              isIconOnly
              color={isRepeat ? 'secondary' : 'default'}
              size="lg"
              title={isRepeat ? t('media.audio.repeatOff') : t('media.audio.repeatOn')}
              onPress={() => setIsRepeat((c) => !c)}
            >
              <IoRepeat />
            </Button>
          </div>

          <div className="max-w-40 grow">
            <Slider
              hideValue
              showTooltip
              aria-label={t('media.audio.speed')}
              classNames={{
                value: 'ml-1 min-w-10',
              }}
              color="foreground"
              formatOptions={{style: 'percent'}}
              maxValue={2}
              minValue={0.25}
              size="sm"
              startContent={<PiMetronomeFill />}
              step={0.05}
              value={speed}
              onChange={(n) => handleChangeSpeed(Array.isArray(n) ? n[0] : n)}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm text-default-700">
            <span>{formatSeconds(decodedAudio?.duration, progress)}</span>
            <span>{formatSeconds(decodedAudio?.duration)}</span>
          </div>
          <Slider
            hideThumb
            aria-label="Player track"
            color="foreground"
            maxValue={1}
            minValue={0}
            step={0.001}
            value={progress}
            onChange={onHandleSeek}
          />
        </div>
      </CardBody>
    </Card>
  );
}

export default MediaAudio;
